import * as React from 'react';
import BPromise from 'bluebird';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE, LIMIT } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import {
  getSubscriptionBalance,
  getSubscriptionExpireDate,
  getSubscriptionPurchaseDate,
  getSubscriptionUsedSmsSegmentsCount
} from 'Src/helpers/accessor/accessor';
import { Subscription } from 'Src/models/subscription';
import { getAllSubscriptions, getSubscriptions, getSubscriptionsCount } from 'Src/helpers/service/admin/subscriptions';
import { SubscriptionReportSettingsModal } from 'Src/components/SubscriptionReportModal/SubscriptionReportSettingsModal';
import { SchoolSettingsTabs } from '../SchoolSettings/SchoolSettingsTabs';
import '../SchoolSettings/SchoolSettings.scss';
import '../BillingPayments.scss';
import moment from 'moment';
import { ListEmptyState } from 'Src/components/ListEmptyState/ListEmptyState';
import { faSms } from '@fortawesome/free-solid-svg-icons';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Type',
    field: 'type',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['type']
  },
  {
    text: 'Status',
    field: 'status',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['status']
  },
  {
    text: 'ID',
    field: 'phoneNumber',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['phoneNumber']
  },
  {
    text: 'Purchased',
    field: 'purchaseDate',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getSubscriptionPurchaseDate
  },
  {
    text: 'Expired',
    field: 'expireDate',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getSubscriptionExpireDate
  },
  {
    text: 'Volume',
    field: 'packageVolume',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['packageVolume']
  },
  {
    text: 'Used',
    field: 'smsCount',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getSubscriptionUsedSmsSegmentsCount
  },
  {
    text: 'Balance',
    field: 'balance',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getSubscriptionBalance
  }
];

interface State {
  items: Subscription[];
  currentPage: number;
  selectedItems: Subscription[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  total: number;
  basePath: string;

  isReportSettingsModalOpen: boolean;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class Subscriptions extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      total: 0,
      basePath: '',

      isReportSettingsModalOpen: false
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    this.setItems().then(res => {
      this.setState({
        isLoading: false
      });
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems().then(res => {
        this.setState({
          isLoading: false
        });
      });
    }
  }

  setItems() {
    const { history, user } = this.props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isShowFilter = isFilterExist2(filters);
    const isDataFiltered = isFilterExist2(filters);

    const where = getServerFieldSectionWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const getItemsPromise = getSubscriptions(user, serverQueryFilter);
    const getItemsCountPromise = getSubscriptionsCount(user, where);

    const promises = [getItemsCountPromise, getItemsPromise];

    return BPromise.all(promises).then(([countObj, items]) => {
      this.setState({
        currentPage: page,
        sortDirection: sortDirection,
        sortColumnsName: sortColumnsName,
        isShowFilter: isShowFilter,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: items,
        total: countObj.count,
        basePath: history.location.pathname
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = getServerFieldSectionWhere(filters);

    getAllSubscriptions(user, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter
    });
  };

  onCloseReportSettingsModal = () => {
    this.setState({
      isReportSettingsModalOpen: false
    });
  };

  renderSubscriptionReportSettingsModal() {
    const { isReportSettingsModalOpen, selectedItems } = this.state;

    const isOneItemSelected = selectedItems.length === 1;

    if (!isOneItemSelected) {
      return null;
    }

    return (
      <SubscriptionReportSettingsModal
        isOpen={isReportSettingsModalOpen}
        onCancelClick={this.onCloseReportSettingsModal}
        subscription={selectedItems[0]}
        user={this.props.user}
      />
    );
  }

  onGetReportClick = () => {
    const { items, selectedItems } = this.state;
    const selectedItemsForReport = selectedItems.length === 1 ? selectedItems : items.length === 1 ? [items[0]] : [];

    if (selectedItemsForReport.length === 1) {
      this.setState({
        selectedItems: selectedItemsForReport,
        isReportSettingsModalOpen: true
      });
    }
  };

  isSubscriptionExpired(subscription: Subscription): boolean {
    if (!subscription.expireDate) return false;

    const rawExpireDate: any = subscription.expireDate;
    const expireDate =
      typeof rawExpireDate === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(rawExpireDate)
        ? moment(rawExpireDate, 'DD-MM-YYYY', true)
        : moment(rawExpireDate);

    return expireDate.isValid() && expireDate.isBefore(moment(), 'day');
  }

  renderSubscriptionStatus(subscription: Subscription): React.ReactNode {
    const status = subscription.status;
    const normalizedStatus = String(status || '').toUpperCase();
    const isExpired = this.isSubscriptionExpired(subscription);
    const isActive = normalizedStatus === 'ACTIVE';
    const className = isExpired
      ? 'schoolBillingBadge schoolBillingBadge--danger'
      : isActive
      ? 'schoolBillingBadge schoolBillingBadge--success'
      : 'schoolBillingBadge';

    return <span className={className}>{isExpired ? 'Expired' : isActive ? 'Active' : status || '-'}</span>;
  }

  renderUsageBar(subscription: Subscription): React.ReactNode {
    const used = Number(getSubscriptionUsedSmsSegmentsCount(subscription, {}) || 0);
    const volume = Number(subscription.packageVolume || 0);
    const percent = volume > 0 ? Math.round((used / volume) * 100) : 0;
    const visualPercent = Math.min(125, percent);
    const barClass =
      percent > 100
        ? 'schoolBillingUsageFill schoolBillingUsageFill--danger schoolBillingUsageFill--over'
        : percent === 100
        ? 'schoolBillingUsageFill schoolBillingUsageFill--danger'
        : percent > 80
        ? 'schoolBillingUsageFill schoolBillingUsageFill--warning'
        : 'schoolBillingUsageFill';

    return (
      <div className="schoolBillingUsage">
        <div className="schoolBillingUsageMeta">
          <span>
            {used.toLocaleString('en-GB')} / {volume.toLocaleString('en-GB')}
          </span>
          <strong>{percent}%</strong>
        </div>
        <div className={`schoolBillingUsageTrack ${percent > 100 ? 'schoolBillingUsageTrack--over' : ''}`}>
          <div className={barClass} style={{ width: `${visualPercent}%` }} />
        </div>
      </div>
    );
  }

  renderBillingPagination(): React.ReactNode {
    const { currentPage, total } = this.state;
    const pageCount = Math.max(1, Math.ceil(total / LIMIT));
    const from = total === 0 ? 0 : (currentPage - 1) * LIMIT + 1;
    const to = Math.min(currentPage * LIMIT, total);
    const isFirst = currentPage <= 1;
    const isLast = currentPage >= pageCount;

    if (pageCount <= 1) return null;

    return (
      <div className="schoolBillingPagination">
        <span>
          {from}-{to} of {total}
        </span>
        <div className="schoolBillingPaginationButtons">
          <button type="button" disabled={isFirst} onClick={() => this.setCurrentPageParams(1)}>
            First
          </button>
          <button type="button" disabled={isFirst} onClick={() => this.setCurrentPageParams(currentPage - 1)}>
            Previous
          </button>
          <strong>
            {currentPage} / {pageCount}
          </strong>
          <button type="button" disabled={isLast} onClick={() => this.setCurrentPageParams(currentPage + 1)}>
            Next
          </button>
          <button type="button" disabled={isLast} onClick={() => this.setCurrentPageParams(pageCount)}>
            Last
          </button>
        </div>
      </div>
    );
  }

  renderSubscriptionsTable(): React.ReactNode {
    const { items, selectedItems, total, isDataFiltered } = this.state;
    const canGetReport = selectedItems.length === 1 || items.length === 1;

    return (
      <>
        <div className="schoolBillingToolbar">
          <div>
            <h2>
              SMS bundles <span>{total}</span>
            </h2>
            <p>Purchased SMS packages, expiry dates and current usage.</p>
          </div>
          <div className="schoolBillingToolbarActions">
            <button
              type="button"
              className="schoolBillingButton schoolBillingButton--secondary"
              disabled={!canGetReport}
              onClick={this.onGetReportClick}
            >
              Usage report
            </button>
          </div>
        </div>
        <div className="schoolBillingTableWrap">
          <table className="schoolBillingTable">
            <thead>
              <tr>
                <th>Package</th>
                <th>Status</th>
                <th>Sender ID</th>
                <th>Purchased</th>
                <th>Expires</th>
                <th>Usage</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="schoolBillingEmpty">
                    <ListEmptyState
                      icon={faSms}
                      title={isDataFiltered ? 'No subscriptions match your filters' : 'No SMS subscriptions yet'}
                      description={
                        isDataFiltered
                          ? 'Try changing or clearing the filters to see other SMS bundles.'
                          : 'Purchased SMS bundles and their usage will appear here.'
                      }
                    />
                  </td>
                </tr>
              ) : (
                items.map((item, index) => {
                  const isSelected = selectedItems.some(selectedItem => selectedItem.id === item.id);
                  return (
                    <tr
                      key={item.id || index}
                      className={isSelected ? 'schoolBillingRow schoolBillingRow--selected' : 'schoolBillingRow'}
                      onClick={() => this.onItemClick(index)}
                    >
                      <td>
                        <div className="schoolBillingStrong">{item.type || '-'}</div>
                        <div className="schoolBillingMono">{item.id}</div>
                      </td>
                      <td>{this.renderSubscriptionStatus(item)}</td>
                      <td className="schoolBillingMono">{item.phoneNumber || '-'}</td>
                      <td>{getSubscriptionPurchaseDate(item, {})}</td>
                      <td>{getSubscriptionExpireDate(item, {})}</td>
                      <td>{this.renderUsageBar(item)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {this.renderBillingPagination()}
      </>
    );
  }
  render() {
    const { isLoading, isReportSettingsModalOpen, items, total } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes = isReportSettingsModalOpen ? 'schoolBillingPage modal-open' : 'schoolBillingPage';
    const activeItems = items.filter(
      item => String((item as any).status || '').toUpperCase() === 'ACTIVE' && !this.isSubscriptionExpired(item)
    );
    const totalVolume = items.reduce((sum, item) => sum + Number((item as any).packageVolume || 0), 0);
    const usedSegments = items.reduce(
      (sum, item) => sum + Number(getSubscriptionUsedSmsSegmentsCount(item, {}) || 0),
      0
    );
    const balance = items.reduce((sum, item) => sum + Number(getSubscriptionBalance(item, {}) || 0), 0);

    return (
      <div className={classes}>
        {this.renderSubscriptionReportSettingsModal()}

        <header className="schoolSettingsHeader">
          <h1>School settings</h1>
          <SchoolSettingsTabs />
        </header>

        <div className="schoolBillingStats">
          <div className={`schoolBillingStatCard ${balance < 0 ? 'schoolBillingStatCard--danger' : ''}`}>
            <div className="schoolBillingStatLabel">
              <span className="schoolBillingStatIcon" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path d="M21 15a3 3 0 0 1-3 3H8l-5 3V6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3z" /></svg>
              </span>
              Credit remaining
            </div>
            <div className={`schoolBillingStatValue ${balance < 0 ? 'schoolBillingStatValue--danger' : ''}`}>
              {balance.toLocaleString('en-GB')}
            </div>
            <div className="schoolBillingStatHint">
              {balance < 0
                ? 'SMS credit limit exceeded'
                : `across ${activeItems.length} active bundle${activeItems.length === 1 ? '' : 's'}`}
            </div>
          </div>
          <div className="schoolBillingStatCard">
            <div className="schoolBillingStatLabel">
              <span className="schoolBillingStatIcon" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path d="M4 19V9M10 19V5M16 19v-7M22 19V3" /></svg>
              </span>
              Used
            </div>
            <div className="schoolBillingStatValue">{usedSegments.toLocaleString('en-GB')}</div>
            <div className="schoolBillingStatHint">of {totalVolume.toLocaleString('en-GB')} purchased segments</div>
          </div>
          <div className="schoolBillingStatCard">
            <div className="schoolBillingStatLabel">
              <span className="schoolBillingStatIcon" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path d="m4 7 8-4 8 4-8 4zM4 7v10l8 4 8-4V7M12 11v10" /></svg>
              </span>
              Packages
            </div>
            <div className="schoolBillingStatValue">{total}</div>
            <div className="schoolBillingStatHint">subscription records</div>
          </div>
        </div>

        <section className="schoolBillingCard">
          {' '}
          <div className="schoolBillingCardBody">{this.renderSubscriptionsTable()}</div>
        </section>
      </div>
    );
  }
}
